import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  inject,
  viewChild,
} from '@angular/core';
import { BrowserStorageService } from '../../service/browser-storage.service';
import { MatAnchor, MatButton, MatIconAnchor } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatRipple } from '@angular/material/core';
import { environment } from '../../../environments/environment';
import { ALIVEAI_FEATURES } from '../features/features.data';
import { NgOptimizedImage } from '@angular/common';
import { catchError, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { PromoResourceService } from '../../../../api-gen/gen-server/services/promo-resource.service';
import { PromoResponse } from '../../../../api-gen/gen-server/models/promo-response';
import { PromptsManagerResourceService } from '../../../../api-gen/gen-server/services/prompts-manager-resource.service';
import { MediaResponse } from '../../../../api-gen/gen/models/media-response';
import { MediaType } from '../../../../api-gen/gen/models/media-type';
import { MODEL_SHOWCASES } from '../model-overview/model-overview.data';
import {
  Partner,
  SocialPartnersComponent,
} from './social-partners/social-partners.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { CountryBlockService } from '../../service/country-block.service';

@Component({
  selector: 'app-home',
  imports: [
    MatButton,
    RouterLink,
    MatIconModule,
    MatRipple,
    MatAnchor,
    MatIconAnchor,
    NgOptimizedImage,
    SocialPartnersComponent,
    MatSlideToggle,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
  private promoApi = inject(PromoResourceService);
  private promptsApi = inject(PromptsManagerResourceService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private countryBlockService = inject(CountryBlockService);

  /** Number of preview thumbnails shown per model card. */
  private readonly modelPreviewCount = 2;
  protected readonly MediaType = MediaType;
  protected readonly modelShowcases = MODEL_SHOWCASES;
  /** Example preview media per model, keyed by showcase slug. */
  protected readonly modelPreviews = signal<
    Partial<Record<string, MediaResponse[]>>
  >({});

  private destroy$ = new Subject<void>();

  showAdultContent = signal(false);

  partners: Partner[] = [
    {
      name: '@_artistry.ai',
      avatar: 'assets/partner/partner1.jpg',
      url: 'https://www.instagram.com/_artistry.ai',
      platform: 'Instagram',
    },
    {
      name: '@lanfe.n',
      avatar: 'assets/partner/partner2.jpg',
      url: 'https://www.instagram.com/lanfe.n',
      platform: 'Instagram',
    },
    {
      name: '@miarayxb',
      avatar: 'assets/partner/partner3.jpg',
      url: 'https://www.instagram.com/miarayxb/',
      platform: 'Instagram',
    },
    {
      name: '@itscamillarinaldi',
      avatar: 'assets/partner/partner4.jpg',
      url: 'https://www.instagram.com/itscamillarinaldi/',
      platform: 'Instagram',
    },
  ];

  readonly galleryContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('galleryContainer');
  readonly heroVideo =
    viewChild.required<ElementRef<HTMLVideoElement>>('heroVideo');

  promoMedia: PromoResponse[] = [];
  promoMediaRow1: PromoResponse[] = [];
  promoMediaRow2: PromoResponse[] = [];
  allMediaRevealed = false;
  currentPage = 0;
  hasNextPage = true;
  isLoading = false;
  scrollPosition = 0;
  private scrollSpeed = 0.7; // pixels per frame
  private animationId?: number;
  private containerWidth = 0;
  private rowWidth = 0;
  private lastMediaCount = 0;

  private storage = inject(BrowserStorageService);

  constructor() {
    // Query-parameter handling is browser-only (window/localStorage) and
    // must not run during SSR/prerendering, where no real visitor exists.
    afterNextRender(() => {
      // if the page has this query parameter ?utm_source=phun then redirect to /gallery
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('admin')) {
        this.storage.setItem('admin', 'true');
      }
      if (urlParams.has('allowuk')) {
        this.storage.setItem('allowuk', 'true');
      }
      if (
        urlParams.has('utm_source') &&
        urlParams.get('utm_source') === 'phun'
      ) {
        window.location.href = '/gallery?utm_source=phun';
      }

      // View-dependent, browser-only init (was ngAfterViewInit):
      // requestAnimationFrame and video.play() don't exist on the server.
      this.calculateDimensions();
      this.startScrollAnimation();
      const heroVideo = this.heroVideo();
      if (heroVideo?.nativeElement) {
        const video = heroVideo.nativeElement;
        video.muted = true;

        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            setTimeout(() => {
              video.play().catch(() => {});
            }, 1000);
          });
        }
      }
    });
  }

  ngOnInit() {
    this.loadPromoMedia();
    this.loadModelPreviews();
    this.countryBlockService.checkAndBlockIfRestricted();
  }

  /**
   * Fetch a couple of example thumbnails for each model card. We only request
   * the first few share slugs per model to keep the payload light.
   */
  private loadModelPreviews() {
    for (const showcase of this.modelShowcases) {
      const shareIds = showcase.shareIds.slice(0, this.modelPreviewCount);
      if (!shareIds.length) {
        continue;
      }
      forkJoin(
        shareIds.map((shareId) =>
          this.promptsApi
            .promptsSharedShareSlugGet({ shareSlug: shareId })
            .pipe(
              map((container) => container.medias?.[0] ?? null),
              catchError(() => of(null)),
            ),
        ),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe((medias) => {
          this.modelPreviews.update((current) => ({
            ...current,
            [showcase.slug]: medias.filter(
              (media): media is MediaResponse => media !== null,
            ),
          }));
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  /**
   * The prompt ID is the media file name, e.g.
   * `.../b33c2eb6-...-09ad.webp?X-Amz-...` -> `b33c2eb6-...-09ad`.
   */
  protected promptIdFromUrl(mediaUrl: string): string {
    const fileName = mediaUrl.split('?')[0].split('/').pop() ?? '';
    return fileName.replace(/\.[^.]+$/, '');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  loadPromoMedia() {
    if (this.isLoading || !this.hasNextPage) return;

    this.isLoading = true;
    const apiCall = this.showAdultContent()
      ? this.promoApi.promosAdultGet({ page: this.currentPage })
      : this.promoApi.promosSafeGet({ page: this.currentPage });

    apiCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        // shuffle the new response

        const newMedia = response.list.sort(() => Math.random() - 0.5) || [];

        this.promoMedia = [...this.promoMedia, ...newMedia];
        this.splitMediaIntoRows();
        this.hasNextPage = response.hasNextPage || false;
        this.currentPage++;
        this.isLoading = false;
        this.changeDetectorRef.markForCheck(); // Ensure the view updates
      },
      error: (error) => {
        console.error('Failed to load promo media:', error);
        this.isLoading = false;
      },
    });
  }

  onAdultContentToggle() {
    // Reset state and reload with the correct API
    this.promoMedia = [];
    this.promoMediaRow1 = [];
    this.promoMediaRow2 = [];
    this.currentPage = 0;
    this.hasNextPage = true;
    this.scrollPosition = 0;
    this.loadPromoMedia();
  }

  splitMediaIntoRows() {
    const array = [...this.promoMedia];

    // Split array media into two arrays, alternating items
    this.promoMediaRow1 = [];
    this.promoMediaRow2 = [];

    array.forEach((media, index) => {
      if (index % 2 === 0) {
        this.promoMediaRow1.push(media);
      } else {
        this.promoMediaRow2.push(media);
      }
    });

    // Recalculate dimensions when media changes
    setTimeout(() => this.calculateDimensions(), 0);
  }

  revealAllMedia() {
    this.allMediaRevealed = true;
  }

  isMediaRevealed(): boolean {
    return this.allMediaRevealed;
  }

  calculateDimensions() {
    const galleryContainer = this.galleryContainer();
    if (galleryContainer) {
      this.containerWidth = galleryContainer.nativeElement.offsetWidth;
      this.rowWidth =
        Math.max(this.promoMediaRow1.length, this.promoMediaRow2.length) * 300;
      this.lastMediaCount = Math.max(
        this.promoMediaRow1.length,
        this.promoMediaRow2.length,
      );
    }
  }

  startScrollAnimation() {
    const animate = () => {
      // Move both rows together in the same direction
      this.scrollPosition -= this.scrollSpeed;

      // Check if dimensions need recalculating (only when media count changes)
      const currentMediaCount = Math.max(
        this.promoMediaRow1.length,
        this.promoMediaRow2.length,
      );
      if (currentMediaCount !== this.lastMediaCount) {
        this.calculateDimensions();
      }

      // Check if we need to load more content (using cached values)
      if (this.containerWidth > 0 && this.rowWidth > 0) {
        if (
          Math.abs(this.scrollPosition) >
          this.rowWidth - this.containerWidth * 2.5
        ) {
          this.loadPromoMedia();
        }

        // Reset position if we've scrolled too far
        if (Math.abs(this.scrollPosition) > this.rowWidth) {
          this.scrollPosition = 0;
        }
      }

      this.animationId = requestAnimationFrame(animate);
      this.changeDetectorRef.markForCheck(); // Ensure the view updates
    };

    this.animationId = requestAnimationFrame(animate);
  }

  protected readonly environment = environment;
  protected readonly ALIVEAI_FEATURES = ALIVEAI_FEATURES;
}
